import React, { ReactElement, useEffect, useRef } from 'react'
import Select from 'react-select'
import Flex from 'typescript-styled-flex'
import { FiSettings } from 'react-icons/fi'
import { MenuPlacement } from 'react-select/src/types'
import { useTranslation } from 'react-i18next'
import FormElement from '../FormElement'
import { IFormElementProps } from '../FormElement/FormInput'
import {
  customContainer,
  customDropdownIndicator,
  customOption,
  customSingleValue,
} from './customSelectComponents'
import { customStyles } from './customStyles'
import { IOptionProps } from './Option'

function selectOnlyValue(onChange: (event: any) => void) {
  return function(values: IChangeValues | IChangeValues[]) {
    if (Array.isArray(values)) {
      const onlyValues = values.map(v => v.value)
      return onChange(onlyValues)
    }

    return onChange(values.value)
  }
}

const SelectInput = FormElement(
  ({
    children,
    name,
    value,
    onChange,
    className,
    noOptionsMessage = 'search_not_found',
    placeholder = 'select',
    withConfig,
    size = 1,
    errorMsg,
    warningMsg,
    isCreatable = false,
    menuPortalTarget = undefined,
    onIconClick,
    menuPlacement,
    disabled = false,
    menuMaxHeight,
    menuMaxWidth,
    isSearchable = false,
    menuPosition = 'absolute',
    defaultOpen,
    staticMenu,
    zIndex = undefined,
    isSmallReservation,
    configIconStyle,
    closeMenuOnScroll = false,
    resetOnSelectedOptionMissing,
  }: ISelectInputProps) => {
    const scrollRef = useRef<any>()
    const { t } = useTranslation()
    const values = React.Children.map(
      children,
      (child: ReactElement<IOptionProps & HTMLOptionElement>) => ({
        icon: child ? child.props.icon : null,
        disableIcon: child ? child.props.disableIcon : null,
        label: child ? child.props.children : null,
        value: child ? child.props.value : null,
        'data-cy': child ? child.props['data-cy'] : null,
        disabled: child ? child.props.disabled : null,
      }),
    ).filter(e => !!e.label)

    const valuesAsArray: React.ReactNode[] = Array.isArray(value)
      ? value
      : [value]
    const selectedValue = values.filter(element =>
      valuesAsArray.includes(element.value),
    )
    const [defaultValue] = values

    // If selected value no longer exists among options, fire onChange with null value.
    // Has custom switch to prevent unexpected behavior in other selects
    useEffect(() => {
      if (resetOnSelectedOptionMissing && !selectedValue.length) {
        onChange(null)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [children])

    function customNoOptionMessage() {
      return t(noOptionsMessage)
    }

    function onScroll(e) {
      return (
        e.target &&
        e.target !== scrollRef.current.select.menuListRef &&
        closeMenuOnScroll
      )
    }

    const onMenuOpen = () => {
      setTimeout(() => {
        const selectedEl = document.getElementsByClassName(
          'MyDropdown__option--is-selected',
        )[0]
        if (selectedEl) {
          selectedEl.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'start',
          })
        }
      }, 200)
    }

    const CustomSelect = () => (
      <Select
        ref={scrollRef}
        tabindex="-1"
        className={className}
        classNamePrefix={className}
        components={{
          DropdownIndicator: customDropdownIndicator(size),
          Option: customOption(size),
          SingleValue: customSingleValue(size),
          ValueContainer: customContainer(size),
        }}
        options={values}
        name={name}
        onMenuOpen={onMenuOpen}
        isDisabled={disabled}
        menuPortalTarget={menuPortalTarget}
        closeMenuOnScroll={onScroll}
        isCreatable={isCreatable}
        menuPlacement={menuPlacement}
        menuPosition={menuPosition}
        value={selectedValue || defaultValue}
        styles={
          !withConfig
            ? customStyles(
                isSmallReservation,
                !!errorMsg,
                menuMaxHeight,
                defaultOpen || staticMenu,
                menuMaxWidth,
                size,
                zIndex,
                !!warningMsg,
              )
            : {
                ...customStyles(
                  isSmallReservation,
                  !!errorMsg,
                  menuMaxHeight,
                  defaultOpen || staticMenu,
                  menuMaxWidth,
                  size,
                  zIndex,
                  !!warningMsg,
                ),
                container: base => ({
                  ...base,
                  width: '100%',
                }),
                dropdownIndicator: base => ({
                  display: base.display,
                  marginRight: `${25 * size}px`,
                  pointerEvents: 'none',
                }),
              }
        }
        isSearchable={isSearchable}
        onChange={selectOnlyValue(onChange) as any}
        noOptionsMessage={customNoOptionMessage}
        placeholder={t(placeholder)}
        menuIsOpen={defaultOpen}
        data-cy={className}
      />
    )

    if (withConfig) {
      return (
        <Flex
          full={true}
          justifyEnd={true}
          alignCenter={true}
          style={{ position: 'relative' }}
        >
          <CustomSelect />
          {withConfig && onIconClick && (
            <Flex
              onClick={onIconClick}
              style={{
                cursor: 'pointer',
                position: 'absolute',
                right: `${10 * size}px`,
                ...configIconStyle,
              }}
            >
              <FiSettings size={16 * size} />
            </Flex>
          )}
        </Flex>
      )
    } else {
      return <CustomSelect />
    }
  },
)

export { SelectInput }

export interface ISelectInputProps extends IFormElementProps {
  children: React.ReactNode
  isCreatable?: boolean
  value?: React.ReactNode | React.ReactNode[]
  isSearchable?: boolean
  placeholder?: string
  noOptionsMessage?: string
  onIconClick?: (e) => void
  configIconStyle?: React.CSSProperties
  withConfig?: boolean
  isSmallReservation?: boolean
  defaultOpen?: boolean
  closeMenuOnScroll?: boolean
  menuPlacement?: MenuPlacement
  staticMenu?: boolean
  menuMaxHeight?: string
  menuMaxWidth?: string
  resetOnSelectedOptionMissing?: boolean
  menuPosition?: 'absolute' | 'fixed'
  menuPortalTarget?: any
  zIndex?: number
}

export interface IChangeValues {
  icon: string | JSX.Element
  label: HTMLCollection
  value: string
}
